import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkspaceObjectTypes, sampleAccounts } from './api';
import type { MergedWorkspace } from '.';
import { useSampleWorkspace } from '@/utils/sample-workspace';
import { authStore } from '@/services/auth/store';
import type { Workspace } from '@/services/auth/types';
import { servicesApi } from '@/services/api';

const DEFAULT_PARAMS = {
  enabled: true,
  includeSampleWorkspace: false,
};

interface UseListWorkspacesParams<T = MergedWorkspace[]> {
  enabled?: boolean;
  select?: (data: MergedWorkspace[]) => T;
}

export const useListWorkspaces = <T = MergedWorkspace[]>(useListWorkspacesParams?: UseListWorkspacesParams<T>) => {
  const params = {
    ...DEFAULT_PARAMS,
    ...(useListWorkspacesParams ?? {}),
  };

  const queryClient = useQueryClient();

  const auth = useStore(authStore);
  const accountId = auth.account?.id;

  const cdpEnabled = useFeatureFlag(FeatureFlag.CdpEnabled);
  const { sampleAccountId, sampleWorkspaceId } = useSampleWorkspace();
  const isInteractiveDemoWorkspaceFeaureFlagEnabled = useFeatureFlag(FeatureFlag.InteractiveDemoWorkspace);
  const isSampleWorkspaceFeatureFlagEnabled = useFeatureFlag(FeatureFlag.SampleWorkspace);
  const isSampleWorkspaceEnabled = isInteractiveDemoWorkspaceFeaureFlagEnabled || isSampleWorkspaceFeatureFlagEnabled;

  return useQuery<MergedWorkspace[], unknown, T, unknown>({
    queryKey: ['workspaces', 'list', isSampleWorkspaceEnabled, sampleAccountId, sampleWorkspaceId],
    queryFn: async () => {
      if (accountId == null) {
        return Promise.reject();
      }

      const { environments: rawWorkspaces } = await servicesApi.getAccountById({
        params: { accountId },
      });

      // If the user has no workspaces available (e.g. users that only handle billing)
      // we should return an empty array instead of throwing an error
      if (rawWorkspaces == null) {
        return [];
      }

      const workspaces: MergedWorkspace[] = rawWorkspaces.map(getMergedWorkspaceFromRaw);

      if (isSampleWorkspaceEnabled && sampleAccountId != null && sampleWorkspaceId != null) {
        const sampleWorkspace: MergedWorkspace = {
          id: sampleWorkspaceId,
          account_id: sampleAccountId,
          cio_workspace_id: sampleWorkspaceId,
          cdp_workspace_id: sampleWorkspaceId,
          cio_initialized: true,
          cdp_initialized: true,
          name: 'Sample Workspace',
          color: '#000000',
        };
        workspaces.push(sampleWorkspace);
      }

      workspaces.forEach((w) => {
        queryClient.setQueryData(
          ['workspaces', { cdp_workspace_id: w.cdp_workspace_id, cio_workspace_id: w.cdp_workspace_id }],
          w,
        );
      });
      return workspaces;
    },
    select: (data: MergedWorkspace[]): T => {
      let filteredData = data;
      if (!cdpEnabled) {
        // Find journeys-only and journeys+pipelines workspaces
        const journeysEnabledWorkspaces = data.filter((w) => w.cio_workspace_id != null && w.cio_workspace_id !== 0);

        // Set cdp_workspace_id to 0 to hide Pipelines workspaces from any lists
        filteredData = journeysEnabledWorkspaces.map((w) => ({
          ...w,
          cdp_workspace_id: 0,
        }));
      }

      if (params.select != null) {
        return params.select(filteredData);
      }

      return filteredData as unknown as T;
    },
    staleTime: 60 * 1000,
    enabled: accountId != null && params.enabled,
  });
};

export const useWorkspaceCount = () => {
  return useListWorkspaces({
    select: (data) => data.length,
  });
};

export const useSampleAccountsQuery = () => {
  return useQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

export const getSampleAccounts = async () => {
  const resp = await sampleAccounts();
  const response = resp?.accounts?.[0] ?? { environment_ids: [] };

  return { accountId: response.id, workspaceId: response.environment_ids[0] };
};

interface UseFilterWorkspacesByNameOptions {
  includeSampleWorkspace?: boolean;
}
export const useFilterWorkspacesByName = (
  name: string,
  { includeSampleWorkspace = false }: UseFilterWorkspacesByNameOptions = {},
) => {
  const lowerCaseName = name.toLowerCase();
  const isInteractiveDemoWorkspaceFeaureFlagEnabled = useFeatureFlag(FeatureFlag.InteractiveDemoWorkspace);
  const isSampleWorkspaceFeatureFlagEnabled = useFeatureFlag(FeatureFlag.SampleWorkspace);
  const { isSampleWorkspace } = useSampleWorkspace();
  const isSampleWorkspaceEnabled = isInteractiveDemoWorkspaceFeaureFlagEnabled || isSampleWorkspaceFeatureFlagEnabled;

  return useListWorkspaces({
    select: (data) => {
      const sampleWorkspace = data.find((w) => isSampleWorkspace(w.cio_workspace_id));

      const shouldIncludeSampleWorkspace = includeSampleWorkspace && sampleWorkspace != null;
      return (
        data
          .filter(
            (w) =>
              w.name.toLowerCase().includes(lowerCaseName) &&
              (!isSampleWorkspaceEnabled || !isSampleWorkspace(w.cio_workspace_id)),
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          // Add demo workspace to the end of the list
          .concat(shouldIncludeSampleWorkspace ? [sampleWorkspace] : [])
      );
    },
  });
};

export const useListWorkspaceObjectTypes = (workspaceId?: number) => {
  const auth = useStore(authStore);
  const accountId = auth.account?.id;

  return useQuery({
    queryKey: ['workspaces', workspaceId, 'object_types'],
    queryFn: async () => {
      if (accountId == null || workspaceId == null) {
        return Promise.reject();
      }

      return listWorkspaceObjectTypes(accountId, workspaceId);
    },
    staleTime: 60 * 1000,
    enabled: accountId != null && workspaceId != null,
  });
};

// TODO: would be great if someone cleaned this up someday
function getMergedWorkspaceFromRaw(workspace: Workspace): MergedWorkspace {
  const workspaceId = +workspace.id;
  return {
    id: workspaceId,
    account_id: +workspace.account_id,
    cio_workspace_id: workspaceId,
    cdp_workspace_id: workspaceId,
    cio_initialized: true,
    cdp_initialized: true,
    name: workspace.name,
    color: workspace.hex_code ?? '',
  };
}
